import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertCircle, ArrowLeft, ChevronRight, Send, CheckCircle2, Link2Off, Clock } from 'lucide-react';
import {
    PreguntaRespuesta,
    SeccionHeader,
    calcularVisibilidadEncuestaPura,
    validarObligatorias,
    convertirRespuestasParaEnvio,
} from './EncuestasProductoView';

/**
 * Página pública de una encuesta de producto para invitados externos
 * (/encuesta/i/:token). No usa login: el link es único y sirve para una sola
 * respuesta. Quien lo abre escribe nombre y apellido y responde.
 *
 * Usa fetch y no axiosInstance a propósito: el interceptor de axios manda a
 * /login ante un 401, y esta página no tiene sesión.
 */

const API = (process.env.REACT_APP_API_URL || '').replace(/\/api\/?$/, '');

const leerBorrador = (token) => {
    try {
        return JSON.parse(window.localStorage.getItem(`encuesta_inv_${token}`) || 'null');
    } catch {
        return null;
    }
};
const guardarBorrador = (token, data) => {
    try { window.localStorage.setItem(`encuesta_inv_${token}`, JSON.stringify(data)); } catch { /* sin storage */ }
};
const borrarBorrador = (token) => {
    try { window.localStorage.removeItem(`encuesta_inv_${token}`); } catch { /* sin storage */ }
};

const fechaLarga = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    return d.toLocaleDateString('es-VE', { day: 'numeric', month: 'long' });
};

const Marco = ({ children }) => (
    <div className="min-h-screen bg-slate-50 text-slate-900">
        <header className="bg-white border-b border-slate-200/70">
            <div className="max-w-2xl mx-auto px-4 sm:px-6 h-14 flex items-center">
                <img src="/logo_sura.png" alt="SURA de Venezuela" className="h-6 w-auto" />
            </div>
        </header>
        <main className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-10">{children}</main>
    </div>
);

const Aviso = ({ icon: Icon, titulo, texto, tono = 'slate' }) => (
    <div className="bg-white rounded-2xl border border-slate-200/70 px-6 py-10 text-center">
        <div className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center ${
            tono === 'teal' ? 'bg-teal-50 text-teal-600' : 'bg-slate-100 text-slate-500'
        }`}
        >
            <Icon size={22} strokeWidth={2} />
        </div>
        {titulo && <h1 className="mt-4 text-lg font-semibold tracking-tight">{titulo}</h1>}
        <p className="mt-1.5 text-sm text-slate-600 leading-relaxed max-w-sm mx-auto">{texto}</p>
    </div>
);

const EncuestaInvitadoView = () => {
    const { token } = useParams();
    const [cargando, setCargando] = useState(true);
    const [estado, setEstado] = useState(null); // {estado, mensaje, titulo} cuando no se puede responder
    const [encuesta, setEncuesta] = useState(null);
    const [paso, setPaso] = useState('datos'); // datos | preguntas | gracias
    const [nombre, setNombre] = useState('');
    const [apellido, setApellido] = useState('');
    const [respuestas, setRespuestas] = useState({});
    const [grupoIdx, setGrupoIdx] = useState(0);
    const [historial, setHistorial] = useState([]);
    const [error, setError] = useState('');
    const [enviando, setEnviando] = useState(false);

    useEffect(() => {
        let vivo = true;
        fetch(`${API}/api/encuestas-publicas/${encodeURIComponent(token)}`)
            .then((r) => r.json())
            .then((d) => {
                if (!vivo) return;
                if (d.estado === 'ok') {
                    setEncuesta(d.encuesta);
                    const b = leerBorrador(token);
                    if (b) {
                        setNombre(b.nombre || '');
                        setApellido(b.apellido || '');
                        setRespuestas(b.respuestas || {});
                    }
                } else {
                    setEstado(d);
                }
            })
            .catch(() => vivo && setEstado({ estado: 'error', mensaje: 'No pudimos cargar la encuesta. Revisa tu conexión e inténtalo de nuevo.' }))
            .finally(() => vivo && setCargando(false));
        return () => { vivo = false; };
    }, [token]);

    // Borrador local: si se cierra la página, al volver sigue donde iba.
    useEffect(() => {
        if (!encuesta || paso === 'gracias') return;
        guardarBorrador(token, { nombre, apellido, respuestas });
    }, [token, encuesta, paso, nombre, apellido, respuestas]);

    const { gruposVisibles, preguntasVisibles } = useMemo(
        () => (encuesta ? calcularVisibilidadEncuestaPura(encuesta, respuestas) : { gruposVisibles: [], preguntasVisibles: new Set() }),
        [encuesta, respuestas]
    );

    const grupos = useMemo(() => {
        const activas = (encuesta?.preguntas || []).filter((p) => !p.archivada);
        return gruposVisibles
            .map((g) => ({
                ...g,
                preguntas: activas.filter((p) => (g.seccion ? String(p.seccionId) === String(g.seccion.id) : !p.seccionId)),
            }))
            .filter((g) => g.preguntas.length > 0);
    }, [encuesta, gruposVisibles]);

    const tieneSecciones = (encuesta?.secciones || []).length > 0;
    const grupoActual = grupos[Math.min(grupoIdx, grupos.length - 1)];
    const esUltimo = !tieneSecciones || grupoIdx >= grupos.length - 1;

    const cambiar = useCallback((id, val) => setRespuestas((prev) => ({ ...prev, [id]: val })), []);

    const empezar = (e) => {
        e.preventDefault();
        if (!nombre.trim() || !apellido.trim()) {
            setError('Escribe tu nombre y tu apellido para empezar.');
            return;
        }
        setError('');
        setPaso('preguntas');
        window.scrollTo({ top: 0 });
    };

    const enviar = async () => {
        setError('');
        const visibles = (encuesta.preguntas || []).filter((p) => !p.archivada && preguntasVisibles.has(p.id));
        const err = validarObligatorias(visibles, respuestas);
        if (err) { setError(err); return; }

        setEnviando(true);
        try {
            const r = await fetch(`${API}/api/encuestas-publicas/${encodeURIComponent(token)}/responder`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    nombre: nombre.trim(),
                    apellido: apellido.trim(),
                    respuestas: convertirRespuestasParaEnvio(visibles, respuestas),
                    preguntas_visibles: Array.from(preguntasVisibles),
                }),
            });
            const d = await r.json().catch(() => ({}));
            if (r.ok) {
                borrarBorrador(token);
                setPaso('gracias');
                window.scrollTo({ top: 0 });
            } else if (d.estado && d.estado !== 'ok') {
                borrarBorrador(token);
                setEstado({ estado: d.estado, mensaje: d.error });
            } else {
                setError(d.error || 'No se pudo enviar. Inténtalo de nuevo.');
            }
        } catch {
            setError('No se pudo enviar. Revisa tu conexión: tus respuestas siguen guardadas en este teléfono.');
        } finally {
            setEnviando(false);
        }
    };

    const siguiente = () => {
        setError('');
        const err = validarObligatorias(grupoActual?.preguntas || [], respuestas);
        if (err) { setError(err); return; }
        setHistorial((h) => [...h, grupoIdx]);
        setGrupoIdx((i) => i + 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const anterior = () => {
        setError('');
        if (historial.length === 0) { setPaso('datos'); return; }
        setGrupoIdx(historial[historial.length - 1]);
        setHistorial((h) => h.slice(0, -1));
    };

    // ---------- Estados sin encuesta ----------

    if (cargando) {
        return (
            <Marco>
                <div className="space-y-4 animate-pulse">
                    <div className="h-40 rounded-2xl bg-slate-200/70" />
                    <div className="h-6 w-2/3 rounded bg-slate-200/70" />
                    <div className="h-24 rounded-2xl bg-slate-200/50" />
                </div>
            </Marco>
        );
    }

    if (estado) {
        const icono = estado.estado === 'respondida' ? CheckCircle2 : estado.estado === 'vencida' ? Clock : Link2Off;
        return (
            <Marco>
                <Aviso
                    icon={icono}
                    tono={estado.estado === 'respondida' ? 'teal' : 'slate'}
                    titulo={estado.titulo}
                    texto={estado.mensaje}
                />
            </Marco>
        );
    }

    if (paso === 'gracias') {
        return (
            <Marco>
                <Aviso
                    icon={CheckCircle2}
                    tono="teal"
                    titulo={`¡Gracias, ${nombre.trim().split(' ')[0]}!`}
                    texto="Tus respuestas quedaron registradas. Ya puedes cerrar esta página."
                />
            </Marco>
        );
    }

    const limite = fechaLarga(encuesta.fechaLimite);

    // ---------- Paso 1: datos ----------

    if (paso === 'datos') {
        return (
            <Marco>
                {encuesta.bannerUrl && (
                    <div className="w-full aspect-[3/1] rounded-2xl overflow-hidden mb-6 bg-slate-100">
                        <img src={encuesta.bannerUrl} alt="" className="w-full h-full object-cover" />
                    </div>
                )}
                <h1 className="text-2xl font-bold tracking-tight text-slate-900 leading-tight">{encuesta.titulo}</h1>
                {encuesta.descripcion && (
                    <p className="mt-2 text-[15px] text-slate-600 leading-relaxed whitespace-pre-wrap">{encuesta.descripcion}</p>
                )}
                {limite && <p className="mt-3 text-sm text-slate-500">Puedes responder hasta el {limite}.</p>}

                <form onSubmit={empezar} className="mt-8 bg-white rounded-2xl border border-slate-200/70 p-5 sm:p-6">
                    <h2 className="text-base font-semibold tracking-tight">Antes de empezar</h2>
                    <p className="mt-1 text-sm text-slate-500">Tu nombre queda junto a tus respuestas.</p>
                    <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <label className="block">
                            <span className="text-sm font-medium text-slate-700">Nombre</span>
                            <input
                                value={nombre}
                                onChange={(e) => setNombre(e.target.value)}
                                autoComplete="given-name"
                                maxLength={80}
                                className="mt-1.5 w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-base outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white"
                            />
                        </label>
                        <label className="block">
                            <span className="text-sm font-medium text-slate-700">Apellido</span>
                            <input
                                value={apellido}
                                onChange={(e) => setApellido(e.target.value)}
                                autoComplete="family-name"
                                maxLength={80}
                                className="mt-1.5 w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-base outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white"
                            />
                        </label>
                    </div>
                    {error && (
                        <p className="mt-4 flex items-center gap-2 text-sm text-rose-600"><AlertCircle size={15} />{error}</p>
                    )}
                    <button
                        type="submit"
                        className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 bg-teal-600 text-white text-[15px] font-medium rounded-xl hover:bg-teal-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500/50"
                    >
                        Comenzar la encuesta
                        <ChevronRight size={17} />
                    </button>
                </form>
            </Marco>
        );
    }

    // ---------- Paso 2: preguntas ----------

    const aMostrar = tieneSecciones ? (grupoActual ? [grupoActual] : []) : grupos;
    const progreso = tieneSecciones && grupos.length > 0 ? Math.round(((grupoIdx + 1) / grupos.length) * 100) : null;

    return (
        <Marco>
            <div className="flex items-start gap-3">
                <button
                    type="button"
                    onClick={anterior}
                    className="mt-0.5 p-2 -ml-2 rounded-lg text-slate-500 hover:bg-slate-200/60"
                    aria-label="Volver"
                >
                    <ArrowLeft size={18} />
                </button>
                <div className="min-w-0">
                    <h1 className="text-lg font-semibold tracking-tight leading-snug">{encuesta.titulo}</h1>
                    <p className="text-sm text-slate-500">Respondiendo como {nombre.trim()} {apellido.trim()}</p>
                </div>
            </div>

            {progreso != null && (
                <div className="mt-5">
                    <div className="flex justify-between text-xs text-slate-500 mb-1.5">
                        <span className="truncate pr-3">{grupoActual?.seccion ? grupoActual.seccion.titulo : 'Preguntas generales'}</span>
                        <span className="tabular-nums">{grupoIdx + 1} de {grupos.length}</span>
                    </div>
                    <div className="h-1.5 bg-slate-200/70 rounded-full overflow-hidden">
                        <motion.div className="h-full bg-teal-500 rounded-full" animate={{ width: `${progreso}%` }} transition={{ duration: 0.3 }} />
                    </div>
                </div>
            )}

            {grupoIdx === 0 && encuesta.imagenExtraUrl && (
                <div className="mt-5 rounded-2xl overflow-hidden border border-slate-200/70 bg-white">
                    <img src={encuesta.imagenExtraUrl} alt="" className="w-full max-h-[420px] object-contain" />
                </div>
            )}

            <AnimatePresence mode="wait">
                <motion.div
                    key={tieneSecciones ? grupoIdx : 'todo'}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="mt-5 space-y-4"
                >
                    {aMostrar.map((g) => (
                        <React.Fragment key={g.key}>
                            {g.seccion && <SeccionHeader seccion={g.seccion} />}
                            {g.preguntas.map((p) => (
                                <PreguntaRespuesta
                                    key={p.id}
                                    pregunta={p}
                                    value={respuestas[p.id]}
                                    onChange={(val) => cambiar(p.id, val)}
                                />
                            ))}
                        </React.Fragment>
                    ))}
                </motion.div>
            </AnimatePresence>

            {error && (
                <div className="mt-5 bg-rose-50 border border-rose-200 text-rose-700 p-4 rounded-xl flex items-start gap-3 text-sm">
                    <AlertCircle size={17} className="mt-0.5 flex-shrink-0" />{error}
                </div>
            )}

            <div className="mt-6 flex justify-end">
                <button
                    type="button"
                    onClick={esUltimo ? enviar : siguiente}
                    disabled={enviando}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 bg-teal-600 text-white text-[15px] font-medium rounded-xl hover:bg-teal-700 disabled:opacity-60 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500/50"
                >
                    {enviando ? (
                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : esUltimo ? <Send size={16} /> : null}
                    {esUltimo ? 'Enviar respuestas' : 'Siguiente'}
                    {!esUltimo && <ChevronRight size={17} />}
                </button>
            </div>
        </Marco>
    );
};

export default EncuestaInvitadoView;
